"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Cpu, Wrench, Bot, ArrowRight, CircleCheck as CheckCircle2, Cog, CircuitBoard } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { cn } from "@/lib/utils";
type Lang = "vi" | "en";
interface StageItem {
  id: string;
  stageNumber: {
    vi: string;
    en: string;
  };
  name: {
    vi: string;
    en: string;
  };
  age: {
    vi: string;
    en: string;
  };
  iconName: string;
  pastelClass: string;
  summary: {
    vi: string;
    en: string;
  };
  coreSkills: {
    vi: string;
    en: string;
  };
  toolset: {
    vi: string;
    en: string;
  };
}
const copy = {
  eyebrow: {
    vi: "Lộ Trình & Kỹ Năng Lab",
    en: "Progression & Lab Skills"
  },
  heading: {
    vi: "Bốn giai đoạn từ mô hình đầu tiên đến Engineering tự hành",
    en: "Four stages from first build to autonomous engineering"
  },
  subheading: {
    vi: "Lộ trình bài bản đồng hành cùng từng bước phát triển của trẻ, trang bị cơ cấu phần cứng thực thụ và kỹ năng Coding chuẩn quốc tế.",
    en: "Follow a clear path that grows with your child, teaching genuine hardware mechanics and real code at every step."
  },
  coreCompetencyLabel: {
    vi: "Năng Lực Cốt Lõi",
    en: "Core Competency"
  },
  labToolsLabel: {
    vi: "Thiết Bị & Công Cụ Lab",
    en: "Lab Hardware & Tools"
  },
  featuredProjectsLabel: {
    vi: "Dự án học viên tiêu biểu",
    en: "Featured Student Projects"
  },
  milestoneLabel: {
    vi: "CỘT MỐC GIAI ĐOẠN",
    en: "STAGE MILESTONE"
  },
  viewProgramLabel: {
    vi: "Xem chương trình",
    en: "View program"
  },
  allStagesFilter: {
    vi: "Tất cả giai đoạn",
    en: "All stages"
  },
  bannerTitle: {
    vi: "Phụ huynh chưa rõ giai đoạn nào phù hợp với năng lực hiện tại của con?",
    en: "Unsure which stage matches your child's experience?"
  },
  bannerDescription: {
    vi: "Đội ngũ chuyên gia Engineering của ROBOTO sẽ thực hiện buổi đánh giá thực hành 30 phút để xếp lớp chuẩn xác vào cấp độ phát triển tối ưu nhất.",
    en: "Our engineering mentors conduct a 30-minute hands-on diagnostic session to place students precisely into their optimal level."
  },
  viewProgramsBtn: {
    vi: "Xem tất cả chương trình",
    en: "View all programs"
  },
  bookTrialBtn: {
    vi: "Đăng ký đánh giá năng lực",
    en: "Book trial placement"
  }
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
const stages: StageItem[] = [{
  id: "stage-4-6",
  stageNumber: {
    vi: "GIAI ĐOẠN 01",
    en: "STAGE 01"
  },
  name: {
    vi: "Khám Phá",
    en: "Explore"
  },
  age: {
    vi: "4–6 tuổi",
    en: "Ages 4–6"
  },
  iconName: "compass",
  pastelClass: "bg-secondary/15 border-secondary/40 text-secondary",
  summary: {
    vi: "Lập trình khối trực quan giúp trẻ làm quen với tư duy chuỗi tuần tự, nhận diện quy luật và trực tiếp khám phá Robotics tương tác.",
    en: "Visual block programming introduces foundational sequencing, pattern recognition, and tactile robotics discovery."
  },
  coreSkills: {
    vi: "Tư duy thuật toán, logic tuần tự nền tảng",
    en: "Algorithmic thinking, basic logic sequencing"
  },
  toolset: {
    vi: "LEGO Education SPIKE Essential & Cubetto",
    en: "LEGO Education SPIKE Essential & Cubetto"
  }
}, {
  id: "stage-7-9",
  stageNumber: {
    vi: "GIAI ĐOẠN 02",
    en: "STAGE 02"
  },
  name: {
    vi: "Chế Tạo",
    en: "Build"
  },
  age: {
    vi: "7–9 tuổi",
    en: "Ages 7–9"
  },
  iconName: "wrench",
  pastelClass: "bg-accent/15 border-accent/40 text-accent",
  summary: {
    vi: "Tỷ số truyền động, nguyên lý mô-men xoắn và độ bền kết cấu được kiểm chứng sinh động qua các mô hình cơ khí gắn động cơ.",
    en: "Gear ratios, torque principles, and structural integrity come to life through motorized mechanical prototypes."
  },
  coreSkills: {
    vi: "Lợi thế cơ học, hộp số, liên kết truyền động motor",
    en: "Mechanical advantage, gearboxes, motorized linkages"
  },
  toolset: {
    vi: "VEX GO & LEGO Technic Mechanics",
    en: "VEX GO & LEGO Technic Mechanics"
  }
}, {
  id: "stage-10-12",
  stageNumber: {
    vi: "GIAI ĐOẠN 03",
    en: "STAGE 03"
  },
  name: {
    vi: "Lập Trình",
    en: "Code"
  },
  age: {
    vi: "10–12 tuổi",
    en: "Ages 10–12"
  },
  iconName: "cpu",
  pastelClass: "bg-secondary/20 border-secondary/50 text-secondary",
  summary: {
    vi: "Học sinh chuyển tiếp sang cú pháp dòng lệnh Python, logic rẽ nhánh có điều kiện và hiệu chuẩn cảm biến vi điều khiển.",
    en: "Students transition to typed Python syntax, conditional logic, and micro-controller sensor calibration."
  },
  coreSkills: {
    vi: "Cú pháp Python, đo đạc dữ liệu cảm biến siêu âm & hồng ngoại",
    en: "Python syntax, ultrasonic & infrared telemetry"
  },
  toolset: {
    vi: "Raspberry Pi Pico & Python Robotics",
    en: "Raspberry Pi Pico & Python Robotics"
  }
}, {
  id: "stage-13-16",
  stageNumber: {
    vi: "GIAI ĐOẠN 04",
    en: "STAGE 04"
  },
  name: {
    vi: "Kỹ Sư",
    en: "Engineer"
  },
  age: {
    vi: "13–16 tuổi",
    en: "Ages 13–16"
  },
  iconName: "bot",
  pastelClass: "bg-primary/20 border-primary/45 text-primary",
  summary: {
    vi: "Điều hướng tự hành, thiết kế firmware nhúng và thị giác máy tính trang bị sẵn sàng cho học viên bước vào các đấu trường Robotics quốc gia & quốc tế.",
    en: "Autonomous navigation, embedded firmware design, and computer vision prepare learners for national robotics arenas."
  },
  coreSkills: {
    vi: "Điều khiển vòng kín PID, thị giác máy tính, động học robot",
    en: "PID closed-loop control, computer vision, kinematics"
  },
  toolset: {
    vi: "VEX V5, Arduino C++, OpenCV",
    en: "VEX V5, Arduino C++, OpenCV"
  }
}];
const stageProgramTargets: Record<string, string[]> = {
  "stage-4-6": ["lego robotics", "lego", "spike"],
  "stage-7-9": ["stem / steam", "stem", "steam"],
  "stage-10-12": ["coding", "python", "lập trình"],
  "stage-13-16": ["robotics", "vex", "kỹ sư"]
};
const stageProjectKeywords: Record<string, string[]> = {
  "stage-4-6": ["robotics"],
  "stage-7-9": ["robotics", "engineering"],
  "stage-10-12": ["coding", "ai"],
  "stage-13-16": ["engineering", "automation"]
};
function StageProjects({
  stageId,
  lang
}: {
  stageId: string;
  lang: Lang;
}) {
  const {
    posts: projectPosts
  } = usePosts();
  const matchingProjects = React.useMemo(() => {
    if (!projectPosts || projectPosts.length === 0) return [];
    const keywords = stageProjectKeywords[stageId] || [];
    return projectPosts.filter(post => {
      const rawDomain = (post as any).acf?.field_domain || post.customFields?.field_domain || "";
      const domainLower = String(rawDomain).toLowerCase();
      return keywords.some(kw => domainLower.includes(kw));
    }).slice(0, 2);
  }, [projectPosts, stageId]);
  if (matchingProjects.length === 0) return null;
  return <div className="mt-3.5 pt-3 border-t border-white/10">
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5 flex items-center justify-between">
        <span>{copy.featuredProjectsLabel[lang]}</span>
        <span className="text-[10px] text-secondary font-mono">
          {matchingProjects.length} {lang === "vi" ? "dự án" : "builds"}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {matchingProjects.map((p, idx) => {
        const title = (p as any).customFields?.project_name || p.title || "Project";
        return <Link key={p.id || idx} data-index={idx} to={p.link || "#"} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 hover:bg-secondary/20 border border-white/10 hover:border-secondary/40 text-[11px] text-slate-300 hover:text-white transition-colors group">
              <span className="size-1 rounded-full bg-secondary shrink-0 group-hover:scale-125 transition-transform" />
              <span className="truncate max-w-[130px] font-sans font-medium">
                {title}
              </span>
            </Link>;
      })}
      </div>
    </div>;
}
function CurriculumContent() {
  const shouldReduceMotion = useReducedMotion();
  const {
    posts
  } = usePosts();
  const [lang, setLang] = React.useState<Lang>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") return stored;
    }
    return "vi";
  });
  const [activeStageId, setActiveStageId] = React.useState<string | null>(null);
  React.useEffect(() => {
    const handleCustomChange = (e: CustomEvent<{
      lang: Lang;
    }>) => {
      if (e.detail?.lang === "vi" || e.detail?.lang === "en") {
        setLang(e.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue as Lang);
      }
    };
    window.addEventListener("roboto-lang-change" as any, handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change" as any, handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const getStageIcon = (iconName: string) => {
    switch (iconName) {
      case "compass":
        return <Compass className="size-7 text-current" strokeWidth={1.75} />;
      case "wrench":
        return <Wrench className="size-7 text-current" strokeWidth={1.75} />;
      case "cpu":
        return <Cpu className="size-7 text-current" strokeWidth={1.75} />;
      case "bot":
      default:
        return <Bot className="size-7 text-current" strokeWidth={1.75} />;
    }
  };
  const getMatchingProgramLink = (stageId: string): string | null => {
    if (!posts || posts.length === 0) return null;
    const targetKeywords = stageProgramTargets[stageId] || [];
    const matchedPost = posts.find(p => {
      const titleLower = (p.title || "").toLowerCase();
      const customNameLower = (p.customFields?.program_name || "").toLowerCase();
      return targetKeywords.some(kw => titleLower.includes(kw) || customNameLower.includes(kw));
    });
    return matchedPost?.link || null;
  };
  const handleStageFilterClick = (stageId: string) => {
    if (activeStageId === stageId) {
      setActiveStageId(null);
      return;
    }
    setActiveStageId(stageId);
    const targetEl = document.getElementById(stageId);
    if (targetEl) {
      targetEl.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const projectsWpQuery = React.useMemo(() => {
    return new WP_Query({
      post_type: "project",
      per_page: 20,
      status: ["publish"]
    });
  }, []);
  return <section id="curriculum-by-stage" data-nav="dark" className="relative bg-[#0d071d] py-20 md:py-24 text-slate-100 overflow-hidden">
      {/* Decorative Lab Blueprint Rail & Subtle Pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] [background-size:32px_32px] [background-image:linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)]" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16">
        {/* Header Block with Technical Spec Accent & Floating Decorative Mascot */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="size-1.5 bg-secondary rounded-sm" />
              <span className="h-px w-10 bg-secondary" />
              <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">
                {copy.eyebrow[lang]}
              </span>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
              {copy.heading[lang]}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed font-sans">
              {copy.subheading[lang]}
            </p>
          </div>

          {/* Static Floating Decorative Mascot */}
          <div className="flex items-center justify-center self-end sm:self-start shrink-0 pointer-events-none select-none z-10 pt-1">
            <div className="relative flex items-center justify-center h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-32 lg:w-32">
              <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} loading="lazy" decoding="async" className="max-h-full max-w-full object-contain bg-transparent drop-shadow-md pointer-events-none select-none" />
            </div>
          </div>
        </div>

        {/* 4 Clickable Age-Range Filter Chips */}
        <div className="mt-8 flex flex-wrap items-center gap-2.5 pb-2">
          {stages.map((stage, i) => {
          const isSelected = activeStageId === stage.id;
          return <Button key={stage.id} data-index={i} type="button" variant="outline" size="sm" onClick={() => handleStageFilterClick(stage.id)} className={cn("rounded-lg font-mono text-xs transition-[border-color,background-color,color,box-shadow] duration-200 cursor-pointer", isSelected ? "border-secondary bg-secondary/20 text-secondary shadow-xs ring-1 ring-secondary/50 font-bold" : "border-white/15 bg-[#170e30] text-slate-300 hover:border-secondary/40 hover:text-white")}>
                <span className="size-1.5 rounded-lg bg-secondary/70 shrink-0" />
                <span>{stage.age[lang]}</span>
                <span className="opacity-60">·</span>
                <span>{stage.name[lang]}</span>
              </Button>;
        })}
        </div>

        {/* Grid Container with Persistent Horizontal Connector Line */}
        <div className="relative mt-8 sm:mt-12">
          {/* Persistent Horizontal Connector Line behind the 4 cards on desktop */}
          <div aria-hidden="true" className="hidden md:block absolute top-[116px] left-8 right-8 h-0.5 bg-gradient-to-r from-secondary/40 via-primary/45 to-accent/40 pointer-events-none z-0">
            <div className="absolute inset-0 flex justify-between items-center px-12">
              <div className="size-2 rounded-lg bg-secondary border border-[#0d071d] shadow-xs" />
              <div className="size-2 rounded-lg bg-accent border border-[#0d071d] shadow-xs" />
              <div className="size-2 rounded-lg bg-secondary border border-[#0d071d] shadow-xs" />
              <div className="size-2 rounded-lg bg-primary border border-[#0d071d] shadow-xs" />
            </div>
          </div>

          {/* 4-Stage Grid wrapped in Projects PostsProvider */}
          <PostsProvider wp_query={projectsWpQuery}>
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.15
          }} className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stages.map((stage, i) => {
              const programLink = getMatchingProgramLink(stage.id);
              const isHighlighted = activeStageId === stage.id;
              return <motion.div key={stage.stageNumber.en} data-index={i} variants={cardVariants} className="h-full flex flex-col">
                    <Card id={stage.id} className={cn("h-full relative flex flex-col justify-between border bg-[#150d2a]/95 rounded-xl p-6 sm:p-7 shadow-lg transition-[border-color,box-shadow,background-color] duration-200 hover:border-secondary/60 hover:shadow-xl", isHighlighted ? "border-secondary ring-2 ring-secondary/60 ring-offset-2 ring-offset-[#0d071d] shadow-xl" : "border-white/10")}>
                      {/* Top Step Gradient Indicator Bar */}
                      <div className="absolute top-0 inset-x-0 h-1.5 bg-white/10 rounded-t-xl overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-primary via-accent to-secondary transition-all" style={{
                      width: `${(i + 1) * 25}%`
                    }} />
                      </div>

                      <div>
                        {/* Stage Meta Chips */}
                        <div className="flex items-center justify-between gap-2 mb-6">
                          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-slate-400">
                            {stage.stageNumber[lang]}
                          </span>
                          <Badge variant="outline" className="rounded-lg border-secondary/40 bg-secondary/15 px-2.5 py-0.5 font-mono text-[11px] font-medium text-secondary">
                            {stage.age[lang]}
                          </Badge>
                        </div>

                        {/* Icon & Stage Title with Gear / Circuit Motif Accent */}
                        <div className="flex items-center gap-3.5 mb-5 relative">
                          <div className="relative">
                            <div className={`flex size-14 shrink-0 items-center justify-center rounded-xl border shadow-2xs ${stage.pastelClass}`}>
                              {getStageIcon(stage.iconName)}
                            </div>
                            {/* Motif accent near icon frame */}
                            <div className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-lg border border-white/15 bg-[#1f133d] shadow-2xs text-secondary">
                              {i % 2 === 0 ? <CircuitBoard className="size-3" strokeWidth={1.5} /> : <Cog className="size-3" strokeWidth={1.5} />}
                            </div>
                          </div>

                          <h3 className="font-serif text-2xl font-bold text-white">
                            {stage.name[lang]}
                          </h3>
                        </div>

                        {/* Core Description */}
                        <p className="text-sm text-slate-300 leading-relaxed mb-6 font-sans">
                          {stage.summary[lang]}
                        </p>

                        {/* Technical Breakdown */}
                        <div className="space-y-3.5 pt-4 border-t border-white/10">
                          <div>
                            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">
                              {copy.coreCompetencyLabel[lang]}
                            </div>
                            <div className="flex items-start gap-2 text-xs text-slate-200 font-medium">
                              <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                              <span>{stage.coreSkills[lang]}</span>
                            </div>
                          </div>

                          <div>
                            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1">
                              {copy.labToolsLabel[lang]}
                            </div>
                            <div className="text-xs text-slate-300 font-sans leading-relaxed">
                              {stage.toolset[lang]}
                            </div>
                          </div>

                          {/* Student Projects Under Lab Hardware & Tools */}
                          <StageProjects stageId={stage.id} lang={lang} />
                        </div>
                      </div>

                      {/* Bottom Card Action: Milestone & Program Link */}
                      <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between text-xs font-semibold text-slate-300 gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-slate-300">{copy.milestoneLabel[lang]}</span>
                          <span className="size-2 rounded-lg bg-secondary" />
                        </div>

                        {programLink && <Link to={programLink} className="inline-flex items-center gap-1 text-xs text-accent hover:text-white font-medium transition-colors underline-offset-4 hover:underline">
                            <span>{copy.viewProgramLabel[lang]}</span>
                            <ArrowRight className="size-3" />
                          </Link>}
                      </div>
                    </Card>
                  </motion.div>;
            })}
            </motion.div>
          </PostsProvider>
        </div>

        {/* Lower Banner CTA Block */}
        <div className="mt-14 rounded-xl border border-white/10 bg-[#160d2e] p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm">
          <div className="max-w-2xl">
            <h4 className="font-serif text-lg sm:text-xl font-bold text-white">
              {copy.bannerTitle[lang]}
            </h4>
            <p className="mt-1 text-sm text-slate-300">
              {copy.bannerDescription[lang]}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="outline" className="font-sans font-semibold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white">
              <Link to="/ProgramsPage">
                {copy.viewProgramsBtn[lang]}
              </Link>
            </Button>
            <Button asChild className="bg-accent text-white hover:bg-accent/90 font-sans font-semibold shadow-xs">
              <Link to="/FreeTrialContactPage">
                {copy.bookTrialBtn[lang]}
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
export default function Curriculum() {
  const wp_query = React.useMemo(() => {
    return new WP_Query({
      post_type: "program",
      per_page: 20,
      status: ["publish"]
    });
  }, []);
  return <PostsProvider wp_query={wp_query}>
      <CurriculumContent />
    </PostsProvider>;
}